import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INSURANCES, SPECIALTIES, SERVICES, LANGUAGES, type Provider } from "@/data/heallexa";
import { MultiTagSelect } from "@/components/forms/MultiTagSelect";
import { Stethoscope, BarChart3, Edit, Crown, Eye, Phone, Globe, MousePointerClick, ExternalLink, Loader2, Mail, Star, CreditCard, Lock, CheckCircle2, Sparkles, Search, MapPin, PlusCircle, CalendarDays, Bot, Clock, Trash2, ShieldCheck, ShieldAlert, XCircle, RefreshCw, Camera } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import {
 loadMyListingsFn,
 loadMyClaimsFn,
 loadMyBookingsFn,
 updateMyListingFn,
 searchProvidersToClaimFn,
 loadSocialClickStatsFn,
} from "./-dashboard.doctor.functions";
import { getVerificationStatus, createVerificationSession, confirmVerificationFromRedirect } from "@/lib/didit-verification.functions";
import { getCredentialsStatus, verifyCredentials, autoVerifyProvider } from "@/lib/credentials-verification.functions";
import { syncListingSubscriptions, createBillingPortal } from "@/server/billing.functions";
import { unclaimListing } from "@/server/listing-delete.functions";
import { deleteMyAccount } from "@/server/account-delete.functions";
import { UpgradeDialog } from "@/components/billing/UpgradeDialog";
import { CityZipInput } from "@/components/forms/CityZipInput";
import { LocationAutocomplete } from "@/components/forms/LocationAutocomplete";
import { SpecialtySelect } from "@/components/forms/SpecialtySelect";
import { AddressAutocomplete } from "@/components/forms/AddressAutocomplete";
import { AIDescriptionField } from "@/components/forms/AIDescriptionField";
import { cityFromLocation } from "@/lib/location-autocomplete";
import { AvailabilityEditor } from "@/components/ai/AvailabilityEditor";
import { PhotoUploader } from "@/components/PhotoUploader";
import { VerifiedBadgeSnippet } from "@/components/provider/VerifiedBadgeSnippet";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StickyPageHeader, PageBody } from "@/components/dashboard/DashboardShell";

export const Route = createFileRoute("/dashboard/doctor")({
 validateSearch: (s: Record<string, unknown>) => ({
 billing: typeof s.billing === "string" ? s.billing : undefined,
 pid: typeof s.pid === "string" ? s.pid : undefined,
 tab: typeof s.tab === "string" ? s.tab : undefined,
 verified: s.verified === "true" || s.verified === true,
 verificationSessionId: typeof s.verificationSessionId === "string" ? s.verificationSessionId : undefined,
 status: typeof s.status === "string" ? s.status : undefined,
 }),
 component: DoctorDashboard,
});

interface ClaimRow {
 id: string; provider_id: string; status: string; created_at: string;
 contact_email: string | null; contact_phone: string | null;
 providers?: { name: string; practice: string } | null;
}

function planLabel(p: Provider) {
 if (p.is_featured && p.featured_subscription_status === "active") return "Featured Premium";
 if (p.plan === "premium_yearly") return "Premium (Yearly)";
 if (p.plan === "premium_monthly") return "Premium (Monthly)";
 if (p.listing_status === "claimed_free" || p.listing_status === "claimed") return "Claimed Free";
 return "Unclaimed";
}

function DoctorDashboard() {
 const { user, loading, signOut } = useAuth();
 const navigate = useNavigate();
 const search = useSearch({ from: "/dashboard/doctor" });
 const [listings, setListings] = useState<Provider[]>([]);
 const [claims, setClaims] = useState<ClaimRow[]>([]);
 const [bookings, setBookings] = useState<any[]>([]);
 const [aiRequests, setAiRequests] = useState<any[]>([]);
 const [editing, setEditing] = useState<Provider | null>(null);
 const validTabs = ["overview", "listings", "calendar", "bookings", "billing", "verification"] as const;
 type TabKey = typeof validTabs[number];
 const tab: TabKey = (validTabs as readonly string[]).includes(search.tab ?? "")
   ? (search.tab as TabKey)
   : "overview";
 const setTab = (t: TabKey) =>
   navigate({ to: "/dashboard/doctor", search: { tab: t === "overview" ? undefined : t } as any, replace: true });
  const [upgrade, setUpgrade] = useState<{ provider: Provider; mode: "premium" | "featured" } | null>(null);
  const [bookingDetail, setBookingDetail] = useState<any | null>(null);
  const [bookingFilter, setBookingFilter] = useState<string>("all");
 const [redirectChecked, setRedirectChecked] = useState(false);

 const sync = useServerFn(syncListingSubscriptions);
 const portal = useServerFn(createBillingPortal);
 const unclaim = useServerFn(unclaimListing);
 const deleteAccount = useServerFn(deleteMyAccount);
 const [deletingId, setDeletingId] = useState<string | null>(null);
 const [deletingAccount, setDeletingAccount] = useState(false);

 const handleDelete = async (p: Provider) => {
  if (!confirm(`Delete "${p.practice}" from your account?\n\nThis will remove the listing from your dashboard. You can re-claim it later.`)) return;
  setDeletingId(p.id);
  try {
   await unclaim({ data: { id: p.id } });
   toast.success("Listing removed");
   reload();
  } catch (e: any) {
   toast.error(e?.message ?? "Failed to delete listing");
  } finally {
   setDeletingId(null);
  }
 };

 const handleDeleteAccount = async () => {
  const confirmed = prompt('This permanently deletes your account, profile, and all listing claims. Type "DELETE" to confirm.');
  if (confirmed !== "DELETE") return;
  setDeletingAccount(true);
  try {
   await deleteAccount();
   toast.success("Account deleted");
   await signOut();
   navigate({ to: "/" });
  } catch (e: any) {
   toast.error(e?.message ?? "Failed to delete account");
   setDeletingAccount(false);
  }
 };

 const reload = async () => {
 if (!user) return;
  const [mine, cl] = await Promise.all([
  // A claimed doctor typically has 1-few listings, but never select 119 cols * N. Use slim shape.
  loadMyListingsFn(),
  loadMyClaimsFn(),
  ]);
  const mineList = (mine ?? []) as Provider[];
  setListings(mineList);
  setClaims((cl ?? []) as any);
  const { bookings: appts, aiRequests: aireqs } = await loadMyBookingsFn();
 setBookings(appts ?? []);
 setAiRequests(aireqs ?? []);
 };

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 (async () => {
 // If returning from Stripe checkout, sync first.
 if (search.billing === "success") {
 try {
 await sync({ data: { provider_id: search.pid } });
 toast.success("Subscription active, your listing is upgraded.");
 } catch (e: any) {
 toast.error(e?.message ?? "Could not sync subscription");
 }
 navigate({ to: "/dashboard/doctor", search: {} as any, replace: true });
 } else if (search.billing === "cancel") {
 toast.message("Checkout canceled.");
 navigate({ to: "/dashboard/doctor", search: {} as any, replace: true });
 } else {
 // Background sync to keep state fresh.
 sync({ data: {} }).catch(() => {});
 }
  await reload();
  setRedirectChecked(true);
  })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  // If after the first load the provider has zero listings, send them to the
  // new-listing form instead of showing an empty dashboard.
  useEffect(() => {
    if (!redirectChecked) return;
    if (listings.length === 0 && search.billing !== "success") {
      navigate({ to: "/dashboard/new-listing", search: {} as any, replace: true });
    }
  }, [redirectChecked, listings.length, navigate, search.billing]);

  if (loading || !user) return <FullLoader />;

 const totalViews = listings.reduce((s, p) => s + (p.profile_views ?? p.views ?? 0), 0);
 const phoneClicks = listings.reduce((s, p) => s + (p.phone_clicks ?? 0), 0);
 const websiteClicks = listings.reduce((s, p) => s + (p.website_clicks ?? 0), 0);
 const bookingClicks = listings.reduce((s, p) => s + (p.booking_clicks ?? 0), 0);
 const premiumCount = listings.filter((p) => p.plan === "premium_monthly" || p.plan === "premium_yearly" || p.is_featured).length;
 const featuredCount = listings.filter((p) => p.is_featured && p.featured_subscription_status === "active").length;

 const openPortal = async () => {
 try {
 const { url } = await portal({ data: { origin: window.location.origin } });
 if (url) window.location.href = url;
 } catch (e: any) {
 toast.error(e?.message ?? "No billing account yet");
 }
 };

 return (
 <div className="min-h-full bg-muted/30">

 <StickyPageHeader
   icon={<Stethoscope className="h-4 w-4 text-primary" />}
   title="Manage your practice"
   subtitle={user.email}
   actions={listings[0] && (() => {
     const p = listings[0];
     const isPremium = (p.plan === "premium_monthly" || p.plan === "premium_yearly") && p.premium_subscription_status === "active";
     const isFeatured = p.is_featured && p.featured_subscription_status === "active";
     if (isFeatured) return null;
     return (
       <>
         {!isPremium && (
           <Button size="sm" onClick={() => setUpgrade({ provider: p, mode: "premium" })}>
             <Crown className="h-4 w-4" /> Upgrade to Premium
           </Button>
         )}
         <Button size="sm" variant="outline" onClick={() => setUpgrade({ provider: p, mode: "featured" })}>
           <Sparkles className="h-4 w-4" /> Get Featured
         </Button>
       </>
     );
   })()}
 />

 <PageBody className="space-y-10">
 {tab === "overview" && (
 <>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 <Metric label="Listings" value={listings.length} icon={<Stethoscope className="h-4 w-4" />} tone="primary" onClick={() => setTab("listings")} />
 <Metric label="Profile views" value={totalViews} icon={<Eye className="h-4 w-4" />} tone="sky" onClick={() => setTab("listings")} />
 <Metric label="Phone clicks" value={phoneClicks} icon={<Phone className="h-4 w-4" />} tone="emerald" onClick={() => setTab("listings")} />
 <Metric label="Website clicks" value={websiteClicks} icon={<Globe className="h-4 w-4" />} tone="violet" onClick={() => setTab("listings")} />
 </div>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 <Metric label="Booking clicks" value={bookingClicks} icon={<MousePointerClick className="h-4 w-4" />} tone="primary" onClick={() => setTab("bookings")} />
 <Metric label="Appointments booked" value={bookings.length} icon={<CalendarDays className="h-4 w-4" />} tone="emerald" onClick={() => setTab("bookings")} />
 <Metric label="Cancelled" value={bookings.filter((b: any) => b.status === "cancelled").length} icon={<Mail className="h-4 w-4" />} tone="rose" onClick={() => { setTab("bookings"); setBookingFilter("cancelled"); }} />
 <Metric label="No show" value={bookings.filter((b: any) => b.status === "no_show").length} icon={<Clock className="h-4 w-4" />} tone="amber" onClick={() => { setTab("bookings"); setBookingFilter("no_show"); }} />
 </div>
 <BookingsTrendChart bookings={bookings} />
 <RecentActivity bookings={bookings} listings={listings} />
 </>
 )}

 {tab === "listings" && (
 <Section
 title="My listings"
 icon={<Stethoscope className="h-4 w-4" />}
 actions={
 <div className="flex gap-2 flex-wrap">
 <Button size="sm" asChild>
 <Link to="/dashboard/new-listing" search={{} as any}><PlusCircle className="h-4 w-4" /> Add Location</Link>
 </Button>
 {listings[0] && (
 <>
 <Button size="sm" variant="outline" onClick={() => setUpgrade({ provider: listings[0], mode: "premium" })}>
 <Crown className="h-4 w-4" /> Upgrade
 </Button>
 <Button size="sm" variant="outline" onClick={() => setUpgrade({ provider: listings[0], mode: "featured" })}>
 <Sparkles className="h-4 w-4" /> Get Featured
 </Button>
 </>
 )}
 </div>
 }
 >
 {listings.length === 0 ? (
 <ClaimSearchPanel />
 ) : (
 <div className="space-y-4">
 {listings.map((p) => (
 <div key={p.id} className="card-surface rounded-md overflow-hidden">
 <PlanBanner provider={p}
 onUpgradePremium={() => setUpgrade({ provider: p, mode: "premium" })}
 onUpgradeFeatured={() => setUpgrade({ provider: p, mode: "featured" })}
 />
 <div className="p-5">
 <div className="flex flex-wrap items-start justify-between gap-3">
 <div className="flex items-start gap-4">
 <img
 src={p.display_photo_url ?? p.profile_photo_url ?? p.logo_url ?? "/avatar-placeholder.png"}
 alt=""
 className="h-14 w-14 rounded-full object-cover border border-border shrink-0"
 loading="lazy"
 decoding="async"
 />
 <div>
 <p className="font-semibold">{p.practice}</p>
 <p className="text-sm text-muted-foreground">{p.specialty} · {p.city}</p>
 <p className="text-xs text-muted-foreground mt-1">
 {(p.profile_views ?? p.views ?? 0)} views · {p.phone_clicks ?? 0} calls · {p.booking_clicks ?? 0} bookings
 </p>
 </div>
 </div>
 <div className="flex gap-2 flex-wrap">
 <Button variant="outline" size="sm" onClick={() => setEditing(p)}><Edit className="h-3 w-3" /> Edit</Button>
 <Button size="sm" asChild><Link to="/providers/$id" params={{ id: p.id }}>View <ExternalLink className="h-3 w-3" /></Link></Button>
 <Button variant="outline" size="sm" onClick={() => handleDelete(p)} disabled={deletingId === p.id} className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30">
  {deletingId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Delete
 </Button>
 </div>
 </div>
 {!p.has_custom_photo && (
 <div className="mt-4 rounded-md border border-dashed border-primary/40 bg-primary/5 p-4 flex flex-wrap items-center justify-between gap-3">
 <div className="text-sm">
 <p className="font-medium">Add a real profile photo</p>
 <p className="text-xs text-muted-foreground">Listings with a real photo get up to 3× more patient bookings.</p>
 </div>
 <PhotoUploader providerId={p.id} compact onUploaded={() => reload()} label="Upload photo" />
 </div>
 )}
 {editing?.id === p.id && (
 <ListingEditor
 provider={editing}
 locked={p.plan === "free"}
 onCancel={() => setEditing(null)}
 onSaved={() => { setEditing(null); reload(); }}
 />
 )}
 {(p.listing_status === "claimed" || p.listing_status === "claimed_free" || p.listing_status === "premium") && (
 <VerifiedBadgeSnippet provider={p} />
 )}
 {(p.plan === "premium_monthly" || p.plan === "premium_yearly") && (
 <SocialAnalytics providerId={p.id} />
 )}
 </div>
 </div>
 ))}
 <div className="pt-2">
 <ClaimSearchPanel compact />
 </div>
 </div>
 )}
 </Section>
 )}

  {tab === "bookings" && (
  <Section
    title="My bookings"
    icon={<CalendarDays className="h-4 w-4" />}
    actions={
      <div className="flex gap-1">
        {["all", "pending", "confirmed", "completed", "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setBookingFilter(s)}
            className={`inline-flex h-6 items-center rounded-sm border px-2 text-[11px] leading-none capitalize transition-colors ${bookingFilter === s ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background text-muted-foreground hover:bg-muted"}`}
          >
            {s}
          </button>
        ))}
      </div>
    }
  >
  {(() => {
    const filtered = bookingFilter === "all" ? bookings : bookings.filter((b) => (b.status ?? "pending").toLowerCase() === bookingFilter);
    if (filtered.length === 0) return <Empty>No bookings match this filter.</Empty>;
    return (
      <div className="card-surface-flat rounded-md overflow-hidden divide-y divide-border">
        {filtered.map((b) => {
          const listing = listings.find((p) => p.id === b.listing_id);
          const when = new Date(b.appointment_at);
          const upcoming = when.getTime() > Date.now();
          return (
            <button
              key={b.id}
              onClick={() => setBookingDetail({ ...b, _listing: listing })}
              className="w-full text-left p-4 flex flex-wrap items-start justify-between gap-3 hover:bg-muted/40 transition"
            >
              <div className="min-w-0">
                <p className="font-medium text-sm">{b.customer_name} <span className="text-muted-foreground font-normal">· {listing?.practice ?? "Listing"}</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {when.toLocaleString()} {b.service_requested ? `· ${b.service_requested}` : ""}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {b.customer_email}{b.customer_phone ? ` · ${b.customer_phone}` : ""}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-xs px-2 py-0.5 rounded-md border ${b.status === "cancelled" ? "border-destructive text-destructive" : b.status === "completed" ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>{b.status}</span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{upcoming ? "Upcoming" : "Past"}</span>
              </div>
            </button>
          );
        })}
      </div>
    );
  })()}
  </Section>
  )}

 {tab === "calendar" && (
 <Section title="My calendar" icon={<Clock className="h-4 w-4" />}>
 {listings.length === 0 ? (
 <Empty>Publish your first listing to manage your calendar.</Empty>
 ) : (
 <div className="space-y-6">
 <CalendarViews bookings={bookings} />
 <div className="space-y-3">
 {listings.map((p) => (
 <AvailabilityEditor key={p.id} providerId={p.id} providerLabel={p.practice ?? p.name} plan={(p as any).plan ?? "free"} onUpgrade={() => setTab("billing")} />
 ))}
 </div>
 </div>
 )}
 </Section>
 )}

 {tab === "billing" && (
 <Section title="Plans & billing" icon={<CreditCard className="h-4 w-4" />}>
 <div className="space-y-4">
 <div className="card-surface rounded-md p-5">
 <div className="flex flex-wrap items-end gap-6 justify-between">
 <div>
 <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-primary">Heallexa Premium</p>
 <p className="text-[28px] font-medium tracking-tight mt-1">$99<span className="text-base font-normal text-muted-foreground">/month</span> <span className="text-sm text-muted-foreground">or $999/year</span></p>
 <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mt-2 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> No per-appointment fees. Ever.</p>
 </div>
 {listings[0] && listings[0].plan === "free" && (
 <Button onClick={() => setUpgrade({ provider: listings[0], mode: "premium" })}><Crown className="h-4 w-4" /> Upgrade now</Button>
 )}
 </div>
 </div>
 {listings.length === 0 && (
 <Empty cta={<Button asChild><Link to="/browse">Find your practice</Link></Button>}>
 Claim a listing first to subscribe.
 </Empty>
 )}
 {listings.map((p) => (
 <div key={p.id} className="card-surface-flat rounded-md p-5">
 <div className="flex flex-wrap items-center justify-between gap-3">
 <div>
 <p className="font-medium">{p.practice}</p>
 <p className="text-xs text-muted-foreground mt-0.5">{planLabel(p)}</p>
 </div>
 <div className="flex gap-2 flex-wrap">
 {p.plan === "free" && <Button onClick={() => setUpgrade({ provider: p, mode: "premium" })}><Crown className="h-4 w-4" /> Upgrade to Premium</Button>}
 {(p.plan === "premium_monthly" || p.plan === "premium_yearly") && !p.is_featured && (
 <Button onClick={() => setUpgrade({ provider: p, mode: "featured" })}><Sparkles className="h-4 w-4" /> Upgrade to Featured</Button>
 )}
 {(p.premium_subscription_status === "active" || p.featured_subscription_status === "active") && (
 <Button variant="outline" onClick={openPortal}><CreditCard className="h-4 w-4" /> Manage subscription</Button>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>

 <div className="mt-10 card-surface-flat rounded-md border-destructive/30 bg-destructive/5 p-5">
  <p className="text-sm font-semibold text-destructive flex items-center gap-2"><Trash2 className="h-4 w-4" /> Danger zone</p>
  <p className="text-xs text-muted-foreground mt-1 max-w-prose">Permanently delete your provider account. This unclaims all your listings, removes your profile, and signs you out. Directory entries remain visible to the public so they can be re-claimed later.</p>
  <Button
   variant="outline"
   size="sm"
   className="mt-3 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/40"
   disabled={deletingAccount}
   onClick={handleDeleteAccount}
  >
   {deletingAccount ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Delete my account
  </Button>
 </div>
 </Section>
 )}

 {tab === "verification" && (
 <Section title="Identity & credentials verification" icon={<ShieldCheck className="h-4 w-4" />}>
 <VerificationTab
   justReturned={search.verified}
   returnedSessionId={search.verificationSessionId}
   returnedStatus={search.status}
 />
 </Section>
 )}
 </PageBody>

 {upgrade && (
 <UpgradeDialog
 open={!!upgrade}
 onOpenChange={(v) => !v && setUpgrade(null)}
 provider={upgrade.provider}
 mode={upgrade.mode}
 />
  )}

  <Sheet open={!!bookingDetail} onOpenChange={(v) => !v && setBookingDetail(null)}>
    <SheetContent className="w-full sm:max-w-md overflow-y-auto">
      {bookingDetail && (
        <>
          <SheetHeader>
            <SheetTitle>Booking details</SheetTitle>
            <SheetDescription>{bookingDetail._listing?.practice ?? "Listing"}</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-5 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Patient</p>
              <p className="font-medium mt-1">{bookingDetail.customer_name}</p>
              <p className="text-muted-foreground">{bookingDetail.customer_email}</p>
              {bookingDetail.customer_phone && <p className="text-muted-foreground">{bookingDetail.customer_phone}</p>}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">When</p>
              <p className="font-medium mt-1">{new Date(bookingDetail.appointment_at).toLocaleString()}</p>
            </div>
            {bookingDetail.service_requested && (
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Service</p>
                <p className="mt-1">{bookingDetail.service_requested}</p>
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Status</p>
              <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-md border capitalize ${bookingDetail.status === "cancelled" ? "border-destructive text-destructive" : bookingDetail.status === "completed" ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>{bookingDetail.status}</span>
            </div>
            {bookingDetail.notes && (
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Notes</p>
                <p className="mt-1 whitespace-pre-wrap">{bookingDetail.notes}</p>
              </div>
            )}
            <div className="pt-4 border-t border-border flex flex-wrap gap-2">
              {bookingDetail.customer_email && (
                <Button asChild size="sm" variant="outline">
                  <a href={`mailto:${bookingDetail.customer_email}`}><Mail className="h-4 w-4" /> Email</a>
                </Button>
              )}
              {bookingDetail.customer_phone && (
                <Button asChild size="sm" variant="outline">
                  <a href={`tel:${bookingDetail.customer_phone}`}><Phone className="h-4 w-4" /> Call</a>
                </Button>
              )}
              {(() => {
                const start = new Date(bookingDetail.appointment_at);
                if (isNaN(start.getTime())) return null;
                const end = new Date(start.getTime() + 30 * 60 * 1000);
                const fmt = (d: Date) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
                const title = encodeURIComponent(`Appointment — ${bookingDetail.customer_name ?? "Patient"}`);
                const details = encodeURIComponent(
                  [
                    bookingDetail.service_requested ? `Service: ${bookingDetail.service_requested}` : "",
                    bookingDetail.customer_email ? `Email: ${bookingDetail.customer_email}` : "",
                    bookingDetail.customer_phone ? `Phone: ${bookingDetail.customer_phone}` : "",
                    bookingDetail.notes ? `Notes: ${bookingDetail.notes}` : "",
                  ].filter(Boolean).join("\n")
                );
                const location = encodeURIComponent(bookingDetail._listing?.practice ?? "");
                const gcal = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}&location=${location}`;
                const outlook = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${title}&startdt=${encodeURIComponent(start.toISOString())}&enddt=${encodeURIComponent(end.toISOString())}&body=${details}&location=${location}`;
                return (
                  <>
                    <Button asChild size="sm" variant="outline">
                      <a href={gcal} target="_blank" rel="noopener noreferrer"><CalendarDays className="h-4 w-4" /> Add to Google</a>
                    </Button>
                    <Button asChild size="sm" variant="outline">
                      <a href={outlook} target="_blank" rel="noopener noreferrer"><CalendarDays className="h-4 w-4" /> Add to Outlook</a>
                    </Button>
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </SheetContent>
  </Sheet>

 </div>
 );
}

function PlanBanner({ provider, onUpgradePremium, onUpgradeFeatured }: { provider: Provider; onUpgradePremium: () => void; onUpgradeFeatured: () => void }) {
 const isFeatured = provider.is_featured && provider.featured_subscription_status === "active";
 const isPremium = !isFeatured && (provider.plan === "premium_monthly" || provider.plan === "premium_yearly") && provider.premium_subscription_status === "active";
 const isFree = !isPremium && !isFeatured;

 if (isFeatured) {
 return (
 <div className="bg-foreground text-background px-5 py-3 flex flex-wrap items-center justify-between gap-3">
 <div className="flex items-center gap-2 text-sm">
 <Star className="h-4 w-4 fill-background text-background" /> <strong>Featured Premium</strong>
 <span className="opacity-70">· Homepage placement active · Verified</span>
 </div>
 </div>
 );
 }
 if (isPremium) {
 return (
 <div className="bg-primary/10 border-b border-primary/30 px-5 py-3 flex flex-wrap items-center justify-between gap-3">
 <div className="flex items-center gap-2 text-sm text-primary">
 <CheckCircle2 className="h-4 w-4" /> <strong>Premium · Verified</strong>
 <span className="text-muted-foreground">· {planLabel(provider)}</span>
 </div>
 <Button size="sm" onClick={onUpgradeFeatured}>
 <Sparkles className="h-3 w-3" /> Upgrade to Featured · $199/mo
 </Button>
 </div>
 );
 }
 // Free
 return (
 <div className="bg-muted/40 border-b border-border px-5 py-3 flex flex-wrap items-center justify-between gap-3">
 <div className="flex items-center gap-2 text-sm">
 <Lock className="h-3.5 w-3.5 text-muted-foreground" />
 <strong>{provider.listing_status === "unclaimed" ? "Unclaimed" : "Claimed Free"}</strong>
 <span className="text-muted-foreground">· Premium features locked</span>
 </div>
 <Button size="sm" onClick={onUpgradePremium}>
 <Crown className="h-3 w-3" /> Upgrade to Premium · from $99/mo
 </Button>
 </div>
 );
}

function ListingEditor({ provider, onCancel, onSaved, locked }: { provider: Provider; onCancel: () => void; onSaved: () => void; locked: boolean }) {
 const [data, setData] = useState({
 practice: provider.practice,
 specialty: provider.specialty,
 phone: provider.phone,
 website: provider.website ?? "",
 
 hours: provider.hours ?? "",
 about: provider.about ?? "",
 short_description: (provider as any).short_description ?? "",
 address: provider.address,
 city: provider.city,
 state: provider.state ?? "",
 insurance: provider.insurance,
 languages: provider.languages,
 services: provider.services,
 logo_url: provider.logo_url ?? "",
 available_days: ((provider as any).available_days ?? []) as string[],
 });
 const [saving, setSaving] = useState(false);

 const toggleDay = (key: string) => {
 setData((d) => ({
 ...d,
 available_days: d.available_days.includes(key)
 ? d.available_days.filter((x) => x !== key)
 : [...d.available_days, key],
 }));
 };

 const toggleInsurance = (id: string) => {
 setData((d) => ({
 ...d,
 insurance: d.insurance.includes(id) ? d.insurance.filter((x) => x !== id) : [...d.insurance, id],
 }));
 };

 const save = async () => {
 setSaving(true);
 try {
 await updateMyListingFn({
 data: {
 id: provider.id,
 payload: {
 practice: data.practice,
 specialty: data.specialty,
 phone: data.phone,
 website: data.website,
 hours: data.hours,
 about: data.about,
 short_description: data.short_description,
 address: data.address,
 city: data.city,
 state: data.state,
 insurance: data.insurance,
 languages: data.languages,
 services: data.services,
 logo_url: data.logo_url,
 available_days: data.available_days,
 },
 },
 });
 toast.success("Listing updated");
 onSaved();
 } catch (e: any) {
 toast.error(e?.message ?? "Failed to update listing");
 } finally {
 setSaving(false);
 }
 };

 return (
 <div className="mt-5 pt-5 border-t border-border space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <Field label="Practice name"><Input value={data.practice} onChange={(e) => setData({ ...data, practice: e.target.value })} /></Field>
 <Field label="Specialty">
 <SpecialtySelect value={data.specialty} onChange={(v) => setData({ ...data, specialty: v })} />
 </Field>
 <Field label="Phone"><Input value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} /></Field>
 <Field label="Website"><Input value={data.website} onChange={(e) => setData({ ...data, website: e.target.value })} /></Field>
 </div>
 <AddressAutocomplete
 address={data.address}
 onPick={({ address, city, state, zip }) =>
 setData((d: any) => ({
 ...d,
 address,
 city: city ?? d.city,
 state: state ?? d.state,
 ...(zip !== undefined ? { zip } : {}),
 }))
 }
 />
 <CityZipInput
 city={data.city ?? ""}
 state={(data as any).state ?? ""}
 zip={(data as any).zip ?? ""}
 onChange={({ city, state, zip }: { city: string; state: string; zip?: string }) =>
 setData({ ...data, city, state, ...(zip !== undefined ? { zip } : {}) } as any)
 }
 />

 <div>
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Available days (shown on your public calendar)</Label>
 <div className="flex flex-wrap gap-2 mt-2">
 {[
 { k: "mon", l: "Mon" }, { k: "tue", l: "Tue" }, { k: "wed", l: "Wed" },
 { k: "thu", l: "Thu" }, { k: "fri", l: "Fri" }, { k: "sat", l: "Sat" }, { k: "sun", l: "Sun" },
 ].map((d) => (
 <button
 type="button"
 key={d.k}
 onClick={() => toggleDay(d.k)}
 className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${data.available_days.includes(d.k) ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}
 >
 {d.l}
 </button>
 ))}
 </div>
 <p className="text-[11px] text-muted-foreground mt-1.5">Selected weekdays will be highlighted blue on your profile's monthly calendar.</p>
 </div>

 <div className={`relative space-y-3 ${locked ? "opacity-60 pointer-events-none" : ""}`}>
 {locked && (
 <div className="absolute -top-2 right-0 text-[10px] uppercase tracking-wider bg-foreground text-background px-2 py-0.5 rounded-sm z-10">
 <Lock className="h-2.5 w-2.5 inline -mt-0.5 mr-1" />Premium only
 </div>
 )}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <Field label="Logo URL (Premium)"><Input value={data.logo_url} onChange={(e) => setData({ ...data, logo_url: e.target.value })} placeholder="https://" /></Field>
 <Field label="Office hours (Premium)"><Input value={data.hours} onChange={(e) => setData({ ...data, hours: e.target.value })} /></Field>
 <Field label="Languages (Premium)">
 <MultiTagSelect value={data.languages} onChange={(v) => setData({ ...data, languages: v })} options={LANGUAGES} placeholder="Select languages…" />
 </Field>
 </div>
 <Field label="Services (Premium)">
 <MultiTagSelect value={data.services} onChange={(v) => setData({ ...data, services: v })} options={SERVICES} placeholder="Select services…" />
 </Field>
 <AIDescriptionField
 label="Short description (tagline)"
 mode="short"
 value={data.short_description}
 onChange={(v) => setData({ ...data, short_description: v })}
 context={{ practice: data.practice, specialty: data.specialty, city: data.city }}
 />
 <AIDescriptionField
 label="About (Premium)"
 mode="about"
 rows={5}
 value={data.about}
 onChange={(v) => setData({ ...data, about: v })}
 context={{ practice: data.practice, specialty: data.specialty, city: data.city }}
 />
 <div>
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Insurance accepted (Premium)</Label>
 <div className="mt-2">
 <MultiTagSelect
 value={data.insurance}
 onChange={(v) => setData({ ...data, insurance: v })}
 options={INSURANCES.map((i) => i.id)}
 placeholder="Select insurance…"
 />
 </div>
 </div>
 </div>

 <div className="flex gap-2 justify-end">
 <Button variant="outline" onClick={onCancel}>Cancel</Button>
 <Button onClick={save} disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes</Button>
 </div>
 </div>
 );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
 return <div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">{label}</Label>{children}</div>;
}
function Section({ title, icon, children, actions }: any) {
 return (
 <section>
 <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
 <h2 className="flex items-center gap-2 text-[20px] font-medium tracking-tight [&_svg]:text-primary">{icon}{title}</h2>
 {actions}
 </div>
 {children}
 </section>
 );
}

function BookingsTrendChart({ bookings }: { bookings: any[] }) {
 const days = 30;
 const today = new Date(); today.setHours(0, 0, 0, 0);
 const data = Array.from({ length: days }).map((_, i) => {
 const d = new Date(today); d.setDate(today.getDate() - (days - 1 - i));
 const key = d.toISOString().slice(0, 10);
 const count = bookings.filter((b) => (b.appointment_at ?? b.created_at ?? "").slice(0, 10) === key).length;
 return { date: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), bookings: count };
 });
 return (
 <div className="card-surface rounded-md p-5">
 <p className="text-[15px] font-medium mb-1">Bookings · last 30 days</p>
 <p className="text-[12px] text-muted-foreground mb-4">{data.reduce((s, d) => s + d.bookings, 0)} total</p>
 <div className="h-56">
 <ResponsiveContainer width="100%" height="100%">
 <AreaChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
 <defs>
 <linearGradient id="bookingsFill" x1="0" y1="0" x2="0" y2="1">
 <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
 <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
 </linearGradient>
 </defs>
 <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
 <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={Math.floor(days / 6)} />
 <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
 <Tooltip />
 <Area type="monotone" dataKey="bookings" stroke="hsl(var(--primary))" fill="url(#bookingsFill)" strokeWidth={2} />
 </AreaChart>
 </ResponsiveContainer>
 </div>
 </div>
 );
}

function RecentActivity({ bookings, listings }: { bookings: any[]; listings: Provider[] }) {
 const recent = [...bookings].slice(0, 8);
 return (
 <div className="card-surface-flat rounded-md overflow-hidden">
 <div className="px-5 py-3 border-b border-border"><p className="text-[15px] font-medium">Recent activity</p></div>
 {recent.length === 0 ? (
 <p className="text-sm text-muted-foreground p-5">No recent bookings yet.</p>
 ) : (
 <ul className="divide-y divide-border">
 {recent.map((b) => {
 const listing = listings.find((p) => p.id === b.listing_id);
 return (
 <li key={b.id} className="px-5 py-3 text-sm flex items-center justify-between gap-3">
 <span><strong>{b.customer_name}</strong> booked {listing?.practice ?? "your listing"}</span>
 <span className="text-xs text-muted-foreground">{new Date(b.appointment_at).toLocaleDateString()}</span>
 </li>
 );
 })}
 </ul>
 )}
 </div>
 );
}

function CalendarViews({ bookings }: { bookings: any[] }) {
  const [view, setView] = useState<"week" | "month">("week");
  const [cursor, setCursor] = useState(() => new Date());

  const byDay = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const b of bookings) {
      const k = (b.appointment_at ?? b.created_at ?? "").slice(0, 10);
      if (!k) continue;
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(b);
    }
    return map;
  }, [bookings]);

  const fmtKey = (d: Date) => d.toISOString().slice(0, 10);

  // Week view: 7 days starting Sunday of cursor
  const weekStart = new Date(cursor);
  weekStart.setDate(cursor.getDate() - cursor.getDay());
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d;
  });

  // Month grid
  const monthFirst = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthGridStart = new Date(monthFirst);
  monthGridStart.setDate(monthFirst.getDate() - monthFirst.getDay());
  const monthDays = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(monthGridStart); d.setDate(monthGridStart.getDate() + i); return d;
  });

  const shift = (n: number) => {
    const d = new Date(cursor);
    if (view === "week") d.setDate(d.getDate() + n * 7);
    else d.setMonth(d.getMonth() + n);
    setCursor(d);
  };

  const todayKey = fmtKey(new Date());
  const label = view === "week"
    ? `${weekDays[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${weekDays[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
    : cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="card-surface-flat rounded-md overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shift(-1)}>‹</Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Today</Button>
          <Button variant="outline" size="sm" onClick={() => shift(1)}>›</Button>
          <span className="ml-2 text-sm font-semibold">{label}</span>
        </div>
        <div className="inline-flex border border-border rounded-md overflow-hidden">
          {(["week", "month"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`h-9 px-3 text-[13px] font-medium transition-colors ${view === v ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
            >
              {v === "week" ? "Week" : "Month"}
            </button>
          ))}
        </div>
      </div>

      {view === "week" ? (
        <div className="grid grid-cols-7 divide-x divide-border">
          {weekDays.map((d) => {
            const k = fmtKey(d);
            const items = byDay.get(k) ?? [];
            const isToday = k === todayKey;
            return (
              <div key={k} className={`min-h-[160px] p-2 ${isToday ? "bg-primary/5" : ""}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  {d.toLocaleDateString(undefined, { weekday: "short" })}
                </div>
                <div className={`text-lg font-semibold ${isToday ? "text-primary" : ""}`}>{d.getDate()}</div>
                <div className="mt-2 space-y-1">
                  {items.slice(0, 4).map((b: any) => (
                    <div key={b.id} className="text-[11px] px-1.5 py-1 bg-primary/10 text-primary truncate" title={b.customer_name}>
                      {new Date(b.appointment_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {b.customer_name}
                    </div>
                  ))}
                  {items.length > 4 && <div className="text-[10px] text-muted-foreground">+{items.length - 4} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-7 border-b border-border">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="p-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 divide-x divide-y divide-border">
            {monthDays.map((d) => {
              const k = fmtKey(d);
              const items = byDay.get(k) ?? [];
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = k === todayKey;
              return (
                <div key={k} className={`min-h-[88px] p-1.5 ${!inMonth ? "bg-muted/30 text-muted-foreground" : ""} ${isToday ? "bg-primary/5" : ""}`}>
                  <div className={`text-xs font-semibold ${isToday ? "text-primary" : ""}`}>{d.getDate()}</div>
                  {items.length > 0 && (
                    <div className="mt-1 text-[10px] px-1.5 py-0.5 bg-primary/15 text-primary inline-block">
                      {items.length} booking{items.length > 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
function Empty({ children, cta }: { children: React.ReactNode; cta?: React.ReactNode }) {
 return <div className="card-surface p-10 text-center rounded-md"><p className="text-muted-foreground text-[14px]">{children}</p>{cta && <div className="mt-4">{cta}</div>}</div>;
}

type DoctorTone = "primary" | "emerald" | "amber" | "rose" | "violet" | "sky";
const DOCTOR_TONE: Record<DoctorTone, { bg: string; icon: string; ring: string; glow: string }> = {
 primary: { bg: "bg-primary/10", icon: "text-primary", ring: "ring-primary/20", glow: "from-primary/15" },
 emerald: { bg: "bg-emerald-500/10", icon: "text-emerald-600 dark:text-emerald-400", ring: "ring-emerald-500/20", glow: "from-emerald-500/15" },
 amber: { bg: "bg-amber-500/10", icon: "text-amber-600 dark:text-amber-400", ring: "ring-amber-500/20", glow: "from-amber-500/15" },
 rose: { bg: "bg-rose-500/10", icon: "text-rose-600 dark:text-rose-400", ring: "ring-rose-500/20", glow: "from-rose-500/15" },
 violet: { bg: "bg-violet-500/10", icon: "text-violet-600 dark:text-violet-400", ring: "ring-violet-500/20", glow: "from-violet-500/15" },
 sky: { bg: "bg-sky-500/10", icon: "text-sky-600 dark:text-sky-400", ring: "ring-sky-500/20", glow: "from-sky-500/15" },
};

function Metric({ label, value, icon, tone = "primary", onClick }: { label: string; value: string | number; icon: React.ReactNode; tone?: DoctorTone; onClick?: () => void }) {
 const t = DOCTOR_TONE[tone];
 const Comp: any = onClick ? "button" : "div";
 return (
  <Comp
   onClick={onClick}
   className={`card-surface rounded-md p-4 sm:p-5 min-w-0 overflow-hidden text-left w-full ${onClick ? "hover-lift cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" : ""}`}
  >
   <div className="flex items-center justify-between gap-2">
    <div className="min-w-0 text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground truncate">{label}</div>
    <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${t.bg} ${t.icon} ring-1 ${t.ring}`}>{icon}</span>
   </div>
   <p className="text-[28px] font-medium mt-3 tracking-tight truncate">{value}</p>
  </Comp>
 );
}
function FullLoader() {
 return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
}

function ClaimSearchPanel({ compact = false }: { compact?: boolean }) {
 const [name, setName] = useState("");
 const [city, setCity] = useState("");
 const [phone, setPhone] = useState("");
 const [results, setResults] = useState<Provider[] | null>(null);
 const [searching, setSearching] = useState(false);
 const [searched, setSearched] = useState(false);

 const onSearch = async (e?: React.FormEvent) => {
 e?.preventDefault();
 if (!name.trim() && !city.trim() && !phone.trim()) {
 toast.message("Enter a name, city, or phone number");
 return;
 }
 setSearching(true);
 try {
 const data = await searchProvidersToClaimFn({
 data: {
 name: name.trim(),
 city: city.trim() ? cityFromLocation(city) : "",
 phoneDigits: phone.trim() ? phone.replace(/\D/g, "") : "",
 },
 });
 setResults((data ?? []) as Provider[]);
 setSearched(true);
 } catch (err: any) {
 toast.error(err?.message ?? "Search failed");
 } finally {
 setSearching(false);
 }
 };

 return (
 <div className="card-surface-flat rounded-md p-5 space-y-4">
 {!compact && (
 <div>
 <p className="text-[15px] font-medium">Find your practice</p>
 <p className="text-[13px] text-muted-foreground">Search by name, city, or phone number to claim your existing listing.</p>
 </div>
 )}
 <form onSubmit={onSearch} className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto]">
 <div className="relative">
 <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
 <Input className="pl-9" placeholder="Practice or doctor name" value={name} onChange={(e) => setName(e.target.value)} />
 </div>
 <div className="relative flex items-center rounded-md border border-input bg-background pl-9 pr-2">
 <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
 <LocationAutocomplete value={city} onChange={setCity} placeholder="City or ZIP" className="flex-1" />
 </div>
 <div className="relative">
 <Phone className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
 <Input className="pl-9" placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} />
 </div>
 <Button type="submit" disabled={searching}>
 {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
 </Button>
 </form>

 {results && (
 <div className="space-y-3">
 {results.length === 0 ? (
 <div className="rounded-md border border-dashed border-border p-6 text-center space-y-3">
 <p className="text-sm text-muted-foreground">No matching practice found.</p>
 <Button asChild>
 <Link to="/list-your-practice"><PlusCircle className="h-4 w-4" /> List your practice</Link>
 </Button>
 </div>
 ) : (
 <>
 <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{results.length} match{results.length === 1 ? "" : "es"}</p>
 <div className="rounded-md border border-border divide-y divide-border overflow-hidden">
 {results.map((p) => {
 const claimed = p.listing_status !== "unclaimed";
 return (
 <div key={p.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
 <div className="min-w-0">
 <p className="font-medium truncate">{p.practice}</p>
 <p className="text-xs text-muted-foreground truncate">{p.name} · {p.specialty} · {p.city}{p.state ? `, ${p.state}` : ""} · {p.phone}</p>
 </div>
 <div className="flex gap-2">
 <Button variant="outline" size="sm" asChild>
 <Link to="/providers/$id" params={{ id: p.id }}>View</Link>
 </Button>
 {claimed ? (
 <Button size="sm" disabled variant="secondary">Already claimed</Button>
 ) : (
 <Button size="sm" asChild>
 <Link to="/providers/$id/claim" params={{ id: p.id }}>Claim</Link>
 </Button>
 )}
 </div>
 </div>
 );
 })}
 </div>
 {searched && (
 <div className="text-center pt-2">
 <p className="text-xs text-muted-foreground mb-2">Don't see your practice?</p>
 <Button variant="outline" asChild>
 <Link to="/list-your-practice"><PlusCircle className="h-4 w-4" /> List your practice</Link>
 </Button>
 </div>
 )}
 </>
 )}
 </div>
 )}
 </div>
 );
}

function SocialAnalytics({ providerId }: { providerId: string }) {
 const [stats, setStats] = useState<Array<{ platform: string; clicks: number }>>([]);
 const [loading, setLoading] = useState(true);
 useEffect(() => {
 let active = true;
 (async () => {
 try {
 const data = await loadSocialClickStatsFn({ data: { providerId } });
 if (!active) return;
 if (data) {
 setStats((data as any[]).map((r) => ({ platform: r.platform, clicks: Number(r.clicks) })));
 }
 } catch {
 /* leave stats empty on failure */
 }
 if (active) setLoading(false);
 })();
 return () => { active = false; };
 }, [providerId]);

 const labels: Record<string, string> = {
 google: "Google", linkedin: "LinkedIn", facebook: "Facebook",
 instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", x: "X",
 };
 const total = stats.reduce((s, r) => s + r.clicks, 0);

 return (
 <div className="mt-4 border-t border-border pt-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-sm font-semibold flex items-center gap-2">
 <BarChart3 className="h-4 w-4 text-primary" /> Social profile clicks
 </h4>
 <span className="text-xs text-muted-foreground">Total: {total}</span>
 </div>
 {loading ? (
 <p className="text-xs text-muted-foreground">Loading…</p>
 ) : stats.length === 0 ? (
 <p className="text-xs text-muted-foreground italic">
 No outbound social clicks yet. Add social links to your profile to start tracking.
 </p>
 ) : (
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
 {stats.map((r) => (
 <div key={r.platform} className="rounded-md border border-border bg-background px-3 py-2">
 <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
 {labels[r.platform] ?? r.platform}
 </div>
 <div className="text-lg font-semibold">{r.clicks}</div>
 </div>
 ))}
 </div>
 )}
 </div>
 );
}

// ── Identity (Didit) + credentials (NPI/sanctions) verification ──────────────
function VerificationTab({ justReturned, returnedSessionId, returnedStatus }: {
  justReturned?: boolean;
  returnedSessionId?: string;
  returnedStatus?: string;
}) {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const fetchStatus = useServerFn(getVerificationStatus);
  const createSession = useServerFn(createVerificationSession);
  const confirmFromRedirect = useServerFn(confirmVerificationFromRedirect);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        // If returning from Didit with a session ID, confirm server-side first
        if (justReturned && returnedSessionId) {
          const confirmed = await confirmFromRedirect({ data: { sessionId: returnedSessionId } });
          if (!cancelled) {
            // Re-fetch full status (includes deadline etc.)
            const s = await fetchStatus();
            if (!cancelled) { setStatus(s); setLoading(false); return; }
          }
        }
        // Normal load or poll
        const poll = async (attempts = 0) => {
          const s = await fetchStatus();
          if (!cancelled) {
            setStatus(s);
            setLoading(false);
            if (justReturned && s?.status !== "approved" && attempts < 8) {
              setTimeout(() => { if (!cancelled) poll(attempts + 1); }, 2500);
            }
          }
        };
        poll();
      } catch {
        if (!cancelled) {
          const s = await fetchStatus().catch(() => null);
          if (!cancelled) { setStatus(s); setLoading(false); }
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const handleStart = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const { url } = await createSession({});
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => { fetchStatus().then(setStatus).catch(() => {}); }, 3000);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to start verification");
    } finally {
      setStarting(false);
    }
  };

  const isVerified = status?.status === "approved";
  const isDeclined = status?.status === "declined";
  const isInProgress = ["in_progress", "in_review", "awaiting_user"].includes(status?.status);
  const isExpired = status?.isExpired;
  const daysRemaining = status?.daysRemaining;

  return (
    <div className="flex flex-col overflow-hidden">
      <div className="flex-none pb-3 border-b border-border flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">Identity &amp; Credentials Verification</span>
        </div>
        <p className="text-xs text-muted-foreground">All claimed accounts must verify within 60 days</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
        {/* LEFT: Didit Identity Verification */}
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            <h3 className="text-sm font-semibold">Identity Verification (Didit)</h3>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div className={`rounded-md border p-4 space-y-3 ${
                isVerified ? "border-green-300 bg-green-50 dark:bg-green-950/20" :
                isDeclined ? "border-red-300 bg-red-50 dark:bg-red-950/20" :
                isExpired ? "border-red-300 bg-red-50 dark:bg-red-950/20" :
                isInProgress ? "border-blue-300 bg-blue-50 dark:bg-blue-950/20" :
                "border-amber-300 bg-amber-50 dark:bg-amber-950/20"
              }`}>
                <div className="flex items-center gap-2.5">
                  <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${
                    isVerified ? "bg-green-100 text-green-700" :
                    isDeclined || isExpired ? "bg-red-100 text-red-700" :
                    isInProgress ? "bg-blue-100 text-blue-700" :
                    "bg-amber-100 text-amber-700"
                  }`}>
                    {isVerified ? <CheckCircle2 className="h-4 w-4" /> :
                     isDeclined || isExpired ? <XCircle className="h-4 w-4" /> :
                     isInProgress ? <Clock className="h-4 w-4" /> :
                     <ShieldAlert className="h-4 w-4" />}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">
                      {isVerified ? "Identity Verified" :
                       isDeclined ? "Verification Declined" :
                       isExpired ? "Deadline Passed" :
                       isInProgress ? "In Progress" :
                       "Verification Required"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isVerified ? `Verified on ${new Date(status.verifiedAt).toLocaleDateString()}` :
                       isDeclined ? "Not approved. Please retry or contact support." :
                       isExpired ? "60-day deadline passed. Listing may be restricted." :
                       isInProgress ? "Under review — usually takes a few minutes." :
                       "Complete to keep your listing active."}
                    </p>
                  </div>
                </div>

                {!isVerified && daysRemaining !== null && daysRemaining !== undefined && (
                  <div className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-sm ${
                    daysRemaining <= 3 ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" :
                    daysRemaining <= 7 ? "bg-amber-100 text-amber-800" :
                    "bg-muted text-muted-foreground"
                  }`}>
                    <Clock className="h-3 w-3 shrink-0" />
                    {isExpired ? "Deadline passed" : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} remaining`}
                  </div>
                )}

                {!isVerified && !isInProgress && (
                  <Button size="sm" onClick={handleStart} disabled={starting}>
                    {starting ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Starting…</> :
                     isDeclined ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Retry</> :
                     <><ShieldCheck className="h-3.5 w-3.5 mr-1.5" />Start Verification</>}
                  </Button>
                )}
                {isInProgress && (
                  <Button size="sm" variant="outline" onClick={() => fetchStatus().then(setStatus).catch(() => {})}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Check Status
                  </Button>
                )}
              </div>

              {!isVerified && (
                <div className="rounded-md border border-border p-3">
                  <p className="text-xs font-medium mb-2">What you'll need</p>
                  <div className="space-y-1.5 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2"><CreditCard className="h-3.5 w-3.5 text-primary shrink-0" /> Government-issued ID (passport, license, or state ID)</div>
                    <div className="flex items-center gap-2"><Camera className="h-3.5 w-3.5 text-primary shrink-0" /> Device with camera for selfie check</div>
                    <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-primary shrink-0" /> Takes about 2 minutes</div>
                  </div>
                </div>
              )}

              <p className="text-[11px] text-muted-foreground">Verified by Didit · HIPAA &amp; GDPR compliant · ID documents not stored</p>
            </>
          )}
        </div>

        {/* RIGHT: Credentials Verification (NPI + sanctions) */}
        <div className="p-6">
          <CredentialsVerificationSection />
        </div>
      </div>
    </div>
  );
}

function CredentialsVerificationSection() {
  const fetchStatus = useServerFn(getCredentialsStatus);
  const runVerify = useServerFn(verifyCredentials);
  const autoVerify = useServerFn(autoVerifyProvider);
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [autoVerifying, setAutoVerifying] = useState(false);
  const [npiInput, setNpiInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<any>(null);
  const autoTriggered = useRef(false);

  const loadStatus = async () => {
    const s = await fetchStatus();
    setStatus(s);
    if ((s as any)?.npi) setNpiInput((s as any).npi);
    if ((s as any)?.accountName) setNameInput((s as any).accountName);
    return s;
  };

  useEffect(() => {
    loadStatus()
      .then(async (s: any) => {
        if (s?.shouldAutoVerify && !autoTriggered.current) {
          autoTriggered.current = true;
          setAutoVerifying(true);
          try {
            const res: any = await autoVerify({});
            setResult(res);
            if (res?.npiVerified && res?.notBanned) {
              toast.success("Credentials auto-verified from NPI registry ✓");
            } else if (res?.skipped) {
              // already verified
            } else if (res?.error) {
              toast.message(`Credential check: ${res.error}`);
            }
            await loadStatus();
          } catch (e: any) {
            toast.error(e?.message ?? "Auto-verification failed");
          } finally {
            setAutoVerifying(false);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerify = async () => {
    if (!npiInput.trim() || npiInput.trim().length !== 10) {
      toast.error("NPI must be exactly 10 digits");
      return;
    }
    if (!nameInput.trim()) {
      toast.error("Account name is required");
      return;
    }
    setVerifying(true);
    setResult(null);
    try {
      const res = await runVerify({ data: { npiNumber: npiInput.trim(), accountName: nameInput.trim() } });
      setResult(res);
      if (res.npiVerified && res.notBanned) {
        toast.success("Credentials verified successfully!");
      } else if (res.error) {
        toast.error(res.error);
      }
      await loadStatus();
    } catch (e: any) {
      toast.error(e?.message ?? "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const isLoading = loading || autoVerifying;
  const grace = status?.gracePeriod;
  const isVerified = status?.credentialsStatus === "verified";
  const isPendingReview = status?.credentialsStatus === "pending_sanction_review";
  const isFailed = status?.credentialsStatus === "failed";
  const isExpired = grace && !grace.withinGrace;
  const urgency = grace?.daysRemaining <= 5 ? "critical" : grace?.daysRemaining <= 10 ? "warning" : "normal";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <h3 className="text-base font-semibold">Credentials Verification</h3>
        </div>
        {status?.npi && (
          <span className="text-xs font-mono text-muted-foreground border border-border px-2 py-0.5 rounded-md">
            NPI: {status.npi}
          </span>
        )}
      </div>

      {autoVerifying && (
        <div className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
          <div>
            <p className="text-sm font-semibold text-primary">Auto-verifying credentials…</p>
            <p className="text-xs text-muted-foreground mt-0.5">Checking NPI {status?.npi} against NPPES federal registry and HHS OIG Exclusion List</p>
          </div>
        </div>
      )}

      {loading && !autoVerifying && (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading credentials status…
        </div>
      )}

      {!isLoading && grace && !isVerified && (
        <div className={`rounded-md border px-4 py-3 flex items-center gap-3 ${
          isExpired ? "border-destructive bg-destructive/5" :
          urgency === "critical" ? "border-red-400 bg-red-50 dark:bg-red-950/20" :
          urgency === "warning" ? "border-amber-400 bg-amber-50 dark:bg-amber-950/20" :
          "border-primary/40 bg-primary/5"
        }`}>
          <Clock className={`h-5 w-5 shrink-0 ${
            isExpired ? "text-destructive" :
            urgency === "critical" ? "text-red-600" :
            urgency === "warning" ? "text-amber-600" : "text-primary"
          }`} />
          <div>
            {isExpired ? (
              <p className="text-sm font-semibold text-destructive">Grace period expired. Contact support immediately.</p>
            ) : (
              <>
                <p className={`text-sm font-semibold ${urgency === "critical" ? "text-red-700 dark:text-red-400" : urgency === "warning" ? "text-amber-700 dark:text-amber-400" : "text-primary"}`}>
                  <span className="text-xl font-bold">{grace.daysRemaining}</span> day{grace.daysRemaining === 1 ? "" : "s"} remaining of your 30-day grace period.
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Day {grace.daysElapsed} of 30 · Verify now to avoid listing suspension.</p>
              </>
            )}
          </div>
        </div>
      )}

      {!isLoading && isVerified && (
        <div className="rounded-md border border-green-300 bg-green-50 dark:bg-green-950/20 p-4 space-y-3">
          <p className="text-sm font-semibold text-green-800 dark:text-green-300 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" /> Credentials verified
            {status?.verifiedAt && <span className="font-normal text-xs text-muted-foreground">on {new Date(status.verifiedAt).toLocaleDateString()}</span>}
          </p>
          <div className="flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 dark:text-green-300 border border-green-400 bg-green-100 dark:bg-green-900/30 px-3 py-1.5 rounded-md">
              <CheckCircle2 className="h-4 w-4" /> NPI Verified
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 dark:text-green-300 border border-green-400 bg-green-100 dark:bg-green-900/30 px-3 py-1.5 rounded-md">
              <CheckCircle2 className="h-4 w-4" /> Sanction Check Cleared (Not Banned)
            </span>
          </div>
        </div>
      )}

      {!isLoading && isPendingReview && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-4 flex items-start gap-3">
          <Clock className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">NPI Verified — Sanctions Check Pending Manual Review</p>
            <p className="text-xs text-muted-foreground mt-1">Our compliance team will complete the review within 24 hours.</p>
            <div className="flex flex-wrap gap-3 mt-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 border border-green-400 bg-green-100 px-3 py-1.5 rounded-md">
                <CheckCircle2 className="h-4 w-4" /> NPI Verified
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 border border-amber-400 bg-amber-100 px-3 py-1.5 rounded-md">
                <Clock className="h-4 w-4" /> Sanction Check — Manual Review Pending
              </span>
            </div>
          </div>
        </div>
      )}

      {!isLoading && (isFailed || (result?.error && !result?.npiVerified)) && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
          <p className="text-sm font-semibold text-destructive flex items-center gap-2">
            <XCircle className="h-4 w-4" /> {result?.error ?? status?.errorMessage}
          </p>
        </div>
      )}

      {!isLoading && result && !result.error && !result.skipped && (
        <div className="flex flex-wrap gap-3">
          {result.npiVerified && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 dark:text-green-300 border border-green-400 bg-green-100 dark:bg-green-900/30 px-3 py-1.5 rounded-md">
              <CheckCircle2 className="h-4 w-4" /> NPI Verified
            </span>
          )}
          {result.notBanned && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 dark:text-green-300 border border-green-400 bg-green-100 dark:bg-green-900/30 px-3 py-1.5 rounded-md">
              <CheckCircle2 className="h-4 w-4" /> Sanction Check Cleared (Not Banned)
            </span>
          )}
        </div>
      )}

      {!isLoading && !isVerified && !isPendingReview && !isExpired && (
        <div className="rounded-md border border-border p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">
              {status?.npi ? "Update NPI or Name" : "Enter Your NPI Number"}
            </h4>
            {status?.npi && !isFailed && (
              <span className="text-xs text-muted-foreground">Pre-filled from your profile</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">We verify your NPI against the federal NPPES registry and check the HHS OIG Exclusion List (LEIE).</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium">NPI Number (10 digits) <span className="text-destructive">*</span></label>
              <Input
                value={npiInput}
                onChange={(e) => setNpiInput(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="1234567890"
                maxLength={10}
                className="font-mono"
              />
              {npiInput.length > 0 && npiInput.length < 10 && (
                <p className="text-[11px] text-destructive">{10 - npiInput.length} more digits needed</p>
              )}
              {npiInput.length === 10 && <p className="text-[11px] text-green-600">✓ Valid length</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Full Name (as on NPI record) <span className="text-destructive">*</span></label>
              <Input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="First Last"
              />
              <p className="text-[11px] text-muted-foreground">Must match your NPPES federal registry name exactly.</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={handleVerify} disabled={verifying || !npiInput || npiInput.length !== 10 || !nameInput}>
              {verifying ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Verifying…</>
              ) : (
                <><ShieldCheck className="h-4 w-4 mr-2" /> Verify Credentials</>
              )}
            </Button>
            {status?.npi && (
              <Button variant="outline" onClick={async () => {
                setAutoVerifying(true);
                try {
                  const res: any = await autoVerify({});
                  setResult(res);
                  await loadStatus();
                  if (res?.npiVerified && res?.notBanned) toast.success("Credentials verified!");
                  else if (res?.error) toast.error(res.error);
                } catch (e: any) {
                  toast.error(e?.message ?? "Verification failed");
                } finally {
                  setAutoVerifying(false);
                }
              }} disabled={autoVerifying}>
                <RefreshCw className="h-4 w-4 mr-2" /> Re-verify from profile
              </Button>
            )}
          </div>

          <div className="rounded-md border border-border p-3 space-y-1 text-xs text-muted-foreground">
            <p className="font-medium text-foreground text-xs">What we check:</p>
            <p>① NPI exists in the NPPES federal registry</p>
            <p>② Your name matches the NPI registry name exactly</p>
            <p>③ You are not listed on the HHS OIG Exclusion List (LEIE)</p>
          </div>
        </div>
      )}
    </div>
  );
}
